'use client'
import { debounce } from '@mui/material'
import { useAtom, useSetAtom } from 'jotai'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  covidDates2020Atom,
  covidDates2021Atom,
  income2019Atom,
  income2020Atom,
  income2021Atom,
  userIdAtom,
  userInfoReducerAtom,
  userTokenAtom,
  userTypeAtom,
} from '../atoms'
import { devlog } from '../utils'
import useQueryUser from './useQueryUser'

export default function useUserInfo(): [
  UserInfo,
  (val: UserInfo) => void,
  boolean,
  {
    requery: () => void
    reloginRequired: boolean
    logout: () => void
  },
] {
  const [userId, setUserId] = useAtom(userIdAtom)
  const [userToken, setUserToken] = useAtom(userTokenAtom)
  const [userType, setUserType] = useAtom(userTypeAtom)
  const [userInfo, setUserInfo] = useAtom(userInfoReducerAtom)
  const [loaded, setLoaded] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const { getUser } = useQueryUser()
  const fetchingRef = useRef(false)
  const [fetching, setFetching] = useState(false)
  const setIncome2019 = useSetAtom(income2019Atom)
  const setIncome2020 = useSetAtom(income2020Atom)
  const setIncome2021 = useSetAtom(income2021Atom)
  const covidDates2020 = useSetAtom(covidDates2020Atom)
  const covidDates2021 = useSetAtom(covidDates2021Atom)
  const [reloginRequired, setReloginRequired] = useState(false)

  useEffect(() => {
    setInitialized(true)
  }, [])

  useEffect(() => {
    if (initialized) {
      if (userInfo.id || !userToken) {
        devlog('useUserInfo >> isLoaded', { 'userInfo.id': userInfo.id, userToken })
        setLoaded(true)
      }
      if (!!userToken && !fetching && !userInfo.id) {
        setFetching(true)
        fetchingRef.current = true
        debounceRequestUser()
        // requestUser()
      }
    }
  }, [initialized, userId, userToken, userInfo?.id])

  const requestUser = async () => {
    devlog('useUserInfo >> requestUser - initial')

    const { error, success, userInfo } = await getUser()
    devlog('useUserInfo >> requestUser', { error, success, userInfo })
    if (success) {
      setUserInfo(userInfo)
      setIncome2019(userInfo.netIncome2019 ?? 0)
      setIncome2020(userInfo.netIncome2020 ?? 0)
      setIncome2021(userInfo.netIncome2021 ?? 0)
      covidDates2020(userInfo.covidDates2020 ?? [])
      covidDates2021(userInfo.covidDates2021 ?? [])
    } else {
      if (error && error.toLocaleLowerCase() === 'invalid token') {
        setReloginRequired(true)
      }
    }
    setFetching(false)
    fetchingRef.current = false

    return userInfo
  }

  const debounceRequestUser = useCallback(debounce(requestUser, 1000), [userToken, userId])

  const logout = () => {
    devlog('useUserInfo >> logout')
    setUserId('')
    setUserType('unknown')
    setUserToken('')
    setUserInfo({ shouldClear: true })
    setReloginRequired(false)
  }

  return [
    userInfo,
    setUserInfo,
    loaded,
    {
      requery: requestUser,
      reloginRequired,
      logout,
    },
  ]
}
